import { useEffect, useState } from "react";
import SideSheetModal from "@/components/SideSheetModal";
import SubSectionHeading from "@/components/SubSectionHeading";
import SegmentedButton from "@/components/SegmentedButton/SegmentedButton";
import TextField from "@/components/TextField";
import Button from "@/components/Button";
import { formatPatTotalScore, getPendingRoundScoreSaves, normalizeRoundScore } from "../_utils";
import type { RegistrantRow } from "../types";

interface EditRegistrantSideSheetProps {
  row: RegistrantRow | null;
  onClose: () => void;
  onAttendanceChange: (value: string | null) => Promise<boolean>;
  onScoreChange: (field: "roundOneScore" | "roundTwoScore", value: string) => Promise<boolean>;
  disabled: boolean;
}

export default function EditRegistrantSideSheet({
  row,
  onClose,
  onAttendanceChange,
  onScoreChange,
  disabled
}: EditRegistrantSideSheetProps) {
  const [roundOneScore, setRoundOneScore] = useState("");
  const [roundTwoScore, setRoundTwoScore] = useState("");
  const [attendanceValue, setAttendanceValue] = useState<string | null>(null);

  useEffect(() => {
    if (!row) return;
    setRoundOneScore(normalizeRoundScore(row.score_18_1));
    setRoundTwoScore(normalizeRoundScore(row.score_18_2));
    setAttendanceValue(row.attended === null ? null : row.attended ? "true" : "false");
  }, [row]);

  const scoresDisabled = disabled || row?.attended === false;
  const displayTotal = row ? formatPatTotalScore(roundOneScore, roundTwoScore, row.score_total) : "--";

  const saveRoundScore = async (field: "roundOneScore" | "roundTwoScore", value: string) => {
    if (!row) return true;
    const normalizedValue = normalizeRoundScore(value);
    const savedValue =
      field === "roundOneScore" ? normalizeRoundScore(row.score_18_1) : normalizeRoundScore(row.score_18_2);

    if (normalizedValue === savedValue) {
      return true;
    }

    const success = await onScoreChange(field, normalizedValue);
    if (!success) {
      if (field === "roundOneScore") {
        setRoundOneScore(savedValue);
      } else {
        setRoundTwoScore(savedValue);
      }
    }

    return success;
  };

  const flushPendingScores = async () => {
    if (!row || scoresDisabled) return;
    for (const { field, value } of getPendingRoundScoreSaves(row, roundOneScore, roundTwoScore)) {
      await saveRoundScore(field, value);
    }
  };

  const handleClose = async () => {
    await flushPendingScores();
    onClose();
  };

  const handleAttendanceChange = async (value: string | null) => {
    const previousValue = attendanceValue;
    setAttendanceValue(value);
    const success = await onAttendanceChange(value);
    if (!success) {
      setAttendanceValue(previousValue);
    }
  };

  return (
    <SideSheetModal
      isSidebarOpen={!!row}
      setSidebarOpen={(open) => {
        if (!open) {
          void handleClose();
        }
      }}
      header={{
        title: row?.registrantName ?? "Edit Registrant"
      }}
      actions={
        <Button variant="outlined" onClick={() => void handleClose()}>
          Done
        </Button>
      }
      disableFocusManagement
    >
      {row && (
        <div className="flex flex-col gap-6 pt-4">
          <div>
            <SubSectionHeading className="mb-2">Attendance</SubSectionHeading>
            <SegmentedButton
              showCheckmark
              allowDeselect
              options={[
                { label: "Present", value: "true" },
                { label: "Absent", value: "false" }
              ]}
              value={attendanceValue}
              onChange={(value) => {
                void handleAttendanceChange(value);
              }}
              disabled={disabled}
            />
          </div>
          <div className="flex flex-col gap-6">
            <SubSectionHeading>Scores</SubSectionHeading>
            <TextField
              label="Round 1"
              value={roundOneScore}
              onChangeText={setRoundOneScore}
              onBlur={() => {
                void saveRoundScore("roundOneScore", roundOneScore);
              }}
              disabled={scoresDisabled}
              trailingIcon={row.meets_qualifying_score_18_1 ? "check" : undefined}
            />
            <TextField
              label="Round 2"
              value={roundTwoScore}
              onChangeText={setRoundTwoScore}
              onBlur={() => {
                void saveRoundScore("roundTwoScore", roundTwoScore);
              }}
              disabled={scoresDisabled}
              trailingIcon={row.meets_qualifying_score_18_2 ? "check" : undefined}
            />
            <div className="text-body-small text-sys-brand-on-surface-variant">
              <span className="inline mr-1 font-medium whitespace-nowrap">Total:</span>
              <span>{displayTotal}</span>
            </div>
          </div>
        </div>
      )}
    </SideSheetModal>
  );
}
